import React, { ReactNode, useEffect, useState } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import ButtonToggleGroup from "../ButtonToggleGroup";
import { IUserProduct } from "../../types/models/user.types";
import PersonalizeAiContainer from "./PersonalizeAiContainer";
import { useRouter } from "next/router";
import { ProductAPI } from "../../client/api/ProductAPI";
import CoreTextField from "../core/CoreTextField";
import CoreLoadingButton from "../core/CoreLoadingButton";
import CoreAddMoreTextFields from "../core/CoreAddMoreTextFields";
import { dateTimeString } from "../../utils/TimeUtils";

function hasNonEmptyValues(obj: Record<string, any>): boolean {
  for (let key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== null && value !== undefined && value !== "") {
        return true;
      }
    }
  }
  return false;
}

const PersonalizeAiAnalyzeWebsite: React.FC<{
  handleProduct: (product: Partial<IUserProduct>) => void;
  redirectUrl: string;
}> = ({ handleProduct, redirectUrl }) => {
  const router = useRouter();
  const [website, setWebsite] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  const handleAnalyze = async () => {
    if (!website) {
      return;
    }
    setLoading(true);
    try {
      const data = await ProductAPI.researchUserProduct(website);
      handleProduct(data.business_context);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography fontFamily="Open Sans Bold" color="primary" variant="h6" gutterBottom>
          Enter Your Website
        </Typography>
        <Typography fontFamily="Open Sans Semi Bold" color="textSecondary" variant="body2">
          We&apos;ll use your website to better understand your business. Input your website home
          page URL below.
        </Typography>
      </Box>
      <CoreTextField
        onChange={(value) => {
          setWebsite(value);
        }}
        value={website}
        label="Website URL"
        placeholder="https://predraft.ai/"
      />
      <Stack direction="row" justifyContent="center" spacing={1}>
        <CoreLoadingButton text="Analyze" onClick={handleAnalyze} loading={loading} />
        <CoreLoadingButton text="Back" variant="text" onClick={() => router.push(redirectUrl)} />
      </Stack>
    </Stack>
  );
};

const PersonalizeAiDetailsForm: React.FC<{
  product?: Partial<IUserProduct>;
  handleProduct: (product: Partial<IUserProduct>) => void;
  redirectUrl?: string;
}> = ({ product, handleProduct }) => {
  const [name, setName] = useState<string | undefined>(product?.name);
  const [description, setDescription] = useState<string | undefined>(product?.description);
  const [contact, setContact] = useState<string | undefined>(product?.contact);
  const [website, setWebsite] = useState<string | undefined>(product?.website);
  const [competitors, setCompetitors] = useState<string[] | undefined>(product?.competitors);
  const [tone, setTone] = useState<string | undefined>(product?.tone);
  const [sitemap, setSitemap] = useState<string | undefined>(product?.sitemap);

  useEffect(() => {
    if (product) {
      setName(product.name);
      setDescription(product.description);
      setContact(product.contact);
      setWebsite(product.website);
      setCompetitors(product.competitors);
      setTone(product.tone);
      setSitemap(product.sitemap);
    }
  }, [product]);

  useEffect(() => {
    handleProduct({
      ...product,
      name,
      description,
      contact,
      website,
      competitors,
      tone,
      sitemap,
    });
  }, [name, description, contact, website, competitors, tone, sitemap]);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography fontFamily="Open Sans Bold" color="primary" variant="h6" sx={{ mb: 1 }}>
          Details
        </Typography>
        <Typography fontFamily="Open Sans Semi Bold" color="textSecondary" variant="body2">
          Fill out as much as you&apos;d like
        </Typography>
      </Box>
      <Stack spacing={1}>
        <Typography fontFamily="Open Sans Bold" color="textSecondary" variant="body2">
          Product Information
        </Typography>
        <CoreTextField
          value={name}
          onChange={(value) => {
            setName(value);
          }}
          label="Product Name"
          placeholder="PredraftAI"
        />
        <CoreTextField
          value={website}
          onChange={(value) => {
            setWebsite(value);
          }}
          label="Website Name"
          placeholder="https://predraft.ai/"
        />
        <CoreTextField
          value={description}
          onChange={(value) => {
            setDescription(value);
          }}
          label="Product Description"
          minRows={4}
          placeholder="SEO Writing Assistant"
        />
      </Stack>
      <Stack spacing={1}>
        <Typography fontFamily="Open Sans Bold" color="textSecondary" variant="body2">
          Contact Details
        </Typography>
        <CoreTextField
          value={contact}
          onChange={(value) => {
            setContact(value);
          }}
          label="Contact Info"
          placeholder="Phone or Email"
        />
      </Stack>
      <Stack spacing={1}>
        <Typography fontFamily="Open Sans Bold" color="textSecondary" variant="body2">
          Competitors
        </Typography>
        <Typography color="textSecondary" variant="body2">
          Providing details about your competitors allows us to tailor content recommendations more
          effectively and ensures we don&apos;t suggest articles or sources from competitor
          websites.
        </Typography>
        <CoreAddMoreTextFields
          onChange={(value) => {
            setCompetitors(value);
          }}
          defaultValues={competitors}
          label="Competitor Website"
          placeholder="https://competitor.com/"
          addMoreButtonText="Add More Competitors"
        />
      </Stack>
      <Stack spacing={1}>
        <Typography fontFamily="Open Sans Bold" color="textSecondary" variant="body2">
          Brand Tone
        </Typography>
        <Typography color="textSecondary" variant="body2">
          Define the tone to establish a consistent voice for your articles.
        </Typography>
        <CoreTextField
          onChange={(value) => {
            setTone(value);
          }}
          value={tone}
          label="Brand Tone"
          placeholder="Informative, friendly"
        />
      </Stack>
      <Stack spacing={1}>
        <Typography fontFamily="Open Sans Bold" color="textSecondary" variant="body2">
          Sitemap
        </Typography>
        <Typography color="textSecondary" variant="body2">
          Provide your sitemap URL, and we&apos;ll identify articles for internal linking.
        </Typography>
        <Grid container alignItems="center">
          <Grid item xs>
            <CoreTextField
              onChange={(value) => {
                setSitemap(value);
              }}
              value={sitemap}
              label="Sitemap Url"
              placeholder="https://example.com/sitemap.xml"
            />
            {product?.sitemap_scanned_at && (
              <Typography color="textSecondary" variant="body2" sx={{ mt: 1 }}>
                Last scanned on: {dateTimeString(product.sitemap_scanned_at, true, "MMMM D, h:mma")}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Stack>
  );
};

export enum ToggleValue {
  WEBSITE_SCAN = "website_scan",
  MANUAL_INPUT = "manual_input",
}

interface Props {
  handleProduct: (product: Partial<IUserProduct>) => void;
  children: ReactNode;
  redirectUrl: string;
  defaultProduct?: Partial<IUserProduct>;
  defaultToggle?: ToggleValue;
}

const PersonalizeAiWebsiteDetailsComponent: React.FC<Props> = ({
  handleProduct,
  defaultProduct,
  defaultToggle,
  children,
  redirectUrl,
}) => {
  const [toggleValue, setToggleValue] = useState<ToggleValue>(
    defaultToggle || ToggleValue.WEBSITE_SCAN
  );
  const [product, setProduct] = useState<Partial<IUserProduct> | undefined>(defaultProduct);

  useEffect(() => {
    if (defaultProduct) {
      setProduct(defaultProduct);
    }
  }, [defaultProduct]);

  useEffect(() => {
    if (product) {
      handleProduct(product);
    }
  }, [product]);

  const handleToggleChange = (value: ToggleValue) => {
    setToggleValue(value);
  };

  return (
    <PersonalizeAiContainer>
      <>
        <Stack spacing={4}>
          <Typography fontFamily="Open Sans Bold" variant="h4" color="primary" align="center">
            Personalize Your AI
          </Typography>
          <Box display="flex" justifyContent="center">
            <ButtonToggleGroup
              value={toggleValue}
              handleValueChange={handleToggleChange}
              buttonToggleItems={[
                {
                  label: "Website Scan",
                  value: ToggleValue.WEBSITE_SCAN,
                  color: "primary",
                },
                {
                  label: "Manual Input",
                  value: ToggleValue.MANUAL_INPUT,
                  color: "primary",
                },
              ]}
            />
          </Box>
        </Stack>

        {toggleValue === ToggleValue.WEBSITE_SCAN && (
          <PersonalizeAiAnalyzeWebsite
            handleProduct={(value) => {
              setProduct(value);
            }}
            redirectUrl={redirectUrl}
          />
        )}
        {(hasNonEmptyValues(product) || toggleValue === ToggleValue.MANUAL_INPUT) && (
          <>
            <PersonalizeAiDetailsForm
              product={product}
              handleProduct={(value) => {
                setProduct(value);
              }}
            />
            {children}
          </>
        )}
      </>
    </PersonalizeAiContainer>
  );
};

export default PersonalizeAiWebsiteDetailsComponent;
